import {
  Alert,
  Box,
  Button,
  Grid,
  InputBaseComponentProps,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { ComponentType, FC, useContext, useState } from 'react';
import { useUpdateProfile } from '@features/profile/hooks/useUpdateProfile';

import StripeInput from '../StripeInput/StripeInput';
import { toast } from 'react-toastify';
import { LoadingButton } from '@mui/lab';
import { authContext } from '@staffedupusa/shared-components';
import { track } from '@amplitude/analytics-browser';
import { amplitudeEventsName } from '@utils/amplitudeEvents';
import { deleteCookie, getCookie } from '@utils/getCookie';
import { useUpdatePlan } from '@features/payments/hooks/useUpdatePlan';
import { useUpdateJobCoupon } from '@features/profile/hooks/useUpdateJobCoupon';

type PaymentFormOnboardingProps = {
  handleClose: () => void;
  submitButtonText: string;
  paymentPeriod?: 'Monthly' | 'Bi-Annual' | 'Annual';
  selectedPlan?: 'starter' | 'smarter' | 'pro';
};

const PaymentFormOnboarding: FC<PaymentFormOnboardingProps> = ({
  handleClose,
  selectedPlan,
  paymentPeriod,
  submitButtonText,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const giveTrail = getCookie('ref_singup_code');

  const { authData } = useContext(authContext);
  const companyId = authData?.user?.company?.id;
  const currentPlan = authData?.user?.company?.plan;

  const [errorText, setErrorText] = useState<string>('');
  const [couponCode, setCouponCode] = useState<string>('');
  const [showCouponCodeFeild, setShowCouponCodeFeild] =
    useState<boolean>(false);
  const { updateProfile } = useUpdateProfile({
    setErrorText,
  });
  const { updateJobStatus } = useUpdateJobCoupon({
    setErrorText,
  });
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);

  const { updatePaymentPlan, isUpdatingPaymentPlan } = useUpdatePlan({
    setErrorText,
  });

  const onSubmit = async (e: any) => {
    e.preventDefault();
    if (couponCode === import.meta.env.VITE_ADMIN_SKIP_PAYMENT_CODE) {
      setIsPaymentLoading(true);
      updateJobStatus();
      updateProfile(
        {
          walkthrough_completed: true,
          has_payment_method: true,
        },
        {
          onSuccess: () => {
            handleClose();
            deleteCookie('signup_cycle');
            deleteCookie('signup_plan');
            setIsPaymentLoading(false);
          },
        },
      );
      return;
    }

    const subscriptionOptions = {
      onSuccess: (data: { status: number }) => {
        if (data.status === 200) {
          const amplitudeEvent = {
            eventType: amplitudeEventsName.SUBSCRIPTION_PLAN_SELECTED,
            eventProperties: {
              description:
                'User selected a subscription cadence and added their credit card.',
              ...(authData.user && { userId: authData.user?.id }),
              ...(selectedPlan && { productId: selectedPlan }),
              ...(paymentPeriod && { 'subscritoin-length': paymentPeriod }),
            },
          };
          track(amplitudeEvent.eventType, amplitudeEvent.eventProperties);
          updateProfile(
            {
              walkthrough_completed: true,
              has_payment_method: true,
            },
            {
              onSuccess: () => {
                deleteCookie('signup_cycle');
                deleteCookie('signup_plan');
                toast.success(
                  giveTrail
                    ? 'Free trial started. Card attached!'
                    : 'Payment Successful',
                );
                deleteCookie('giveTrail');
                setIsPaymentLoading(false);
                handleClose();
              },
            },
          );
        } else {
          setIsPaymentLoading(false);
        }
      },
      onError: () => {
        setIsPaymentLoading(false);
      },
    };
    if (!stripe || !elements) {
      setErrorText('No Token Found');
      return;
    }
    const cardElement = elements.getElement(CardNumberElement);
    if (cardElement) {
      const { token } = await stripe.createToken(cardElement);
      if (token) {
        setErrorText('');
        setIsPaymentLoading(true);
      }
      if (token && selectedPlan) {
        const selectedPlanId =
          selectedPlan === 'starter'
            ? 4
            : selectedPlan === 'smarter'
              ? 1
              : selectedPlan === 'pro'
                ? 2
                : 3;
        const selectedBillingCycle =
          selectedPlan === 'starter'
            ? 4
            : paymentPeriod === 'Monthly'
              ? 1
              : paymentPeriod === 'Bi-Annual'
                ? 2
                : 3;
            updatePaymentPlan(
              {
                token: token.id as string,
                billing_cycle: selectedBillingCycle,
                ...(giveTrail && selectedPlanId === 4 ? { give_trial: true } :{}),
                ...(showCouponCodeFeild && couponCode
                  ? { coupon_code: couponCode }
                  : {}),
                plan_ids: [
                  {
                    company_id: companyId,
                    plan_id: currentPlan,
                    new_plan_id: selectedPlanId,
                  },
                ],
              },
              subscriptionOptions,
            );
      }
    }
  };
  return (
    <form style={{ width: '100%' }} onSubmit={onSubmit}>
      <Box sx={{ textAlign: 'center' }}>
        <Typography
          mt={1}
          mb={0}
          textAlign="center"
          color="gray"
          sx={{
            fontStyle: 'italic',
            fontSize: '0.8rem',
            cursor: 'pointer',
          }}
          onClick={() => setShowCouponCodeFeild(true)}
        >
          Have a coupon code?
        </Typography>
      </Box>
      <Stack sx={{ width: '100%' }} spacing={2}>
        {errorText && <Alert severity="error">{errorText}</Alert>}
        <Grid
          sx={{ margin: '0 important', width: '100%' }}
          container
          spacing={2}
          rowGap={1}
        >
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              InputProps={{
                inputComponent:
                  StripeInput as ComponentType<InputBaseComponentProps>,
                inputProps: {
                  component: CardNumberElement,
                },
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              InputProps={{
                inputComponent:
                  StripeInput as ComponentType<InputBaseComponentProps>,
                inputProps: {
                  component: CardExpiryElement,
                },
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              InputProps={{
                inputComponent:
                  StripeInput as ComponentType<InputBaseComponentProps>,
                inputProps: {
                  component: CardCvcElement,
                },
              }}
            />
          </Grid>
        </Grid>
        {showCouponCodeFeild && (
          <Box
            width="97%"
            m={2}
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{
              marginInline: '10px !important',
              '& .MuiFormControl-root ': {
                width: '97%',
              },
            }}
          >
            <TextField
              sx={{
                '& .MuiInputBase-root > input': {
                  height: '1.0rem',
                  width: '100%',
                },
              }}
              onChange={(e) => setCouponCode(e.target.value)}
              size="small"
              placeholder="coupon code"
            />
            <Button
              sx={{ fontSize: '0.8rem' }}
              size="small"
              onClick={() => setShowCouponCodeFeild(false)}
            >
              Cancel
            </Button>
          </Box>
        )}
        <LoadingButton
          type="submit"
          variant="contained"
          fullWidth
          sx={{ mt: 2, width: '97%', marginInline: '10px !important' }}
          loading={isPaymentLoading || isUpdatingPaymentPlan}
        >
          {submitButtonText}
        </LoadingButton>
      </Stack>
    </form>
  );
};

export default PaymentFormOnboarding;
